<script lang="ts">
  import { followLink } from './host';
  import { parseSkoolContent, type Inline } from './skoolContent';

  // Renders Skool post/comment content: paragraphs, soft breaks, links, @mentions,
  // bold, and ordered/unordered lists — from a parsed token tree, so no raw HTML is
  // ever injected. Typography (size, colour, leading) comes from the caller's class.
  //
  // `inverted` is for content sitting on a FILLED background — the owner's own DM
  // bubble is `bg-primary`, and the default token colours are all drawn from that same
  // primary: a link was `text-primary` on `bg-primary`, which is a link nobody can see,
  // and a mention was `bg-primary/10 text-primary`, which is worse because it looks
  // like a deliberate highlight of nothing. Inverted, every token takes the surrounding
  // text colour and separates itself by weight and underline instead of by hue.
  //
  // A prop rather than a CSS override in the caller: contrast is decided by whoever
  // painted the background, and reaching into this component's markup with `:global`
  // would couple that caller to token classes it does not own.
  let {
    text,
    class: cls = '',
    inverted = false,
  }: { text: string | null | undefined; class?: string; inverted?: boolean } = $props();
  const blocks = $derived(parseSkoolContent(text));

  const boldCls = $derived(inverted ? 'font-semibold' : 'font-semibold text-base-content');
  const codeCls = $derived(
    inverted
      ? 'rounded bg-current/15 px-1 py-0.5 font-mono text-[0.85em]'
      : 'rounded bg-base-content/10 px-1 py-0.5 font-mono text-[0.85em]',
  );
  const mentionCls = $derived(
    inverted
      ? 'rounded bg-current/15 px-1 font-medium'
      : 'rounded bg-primary/10 px-1 font-medium text-primary',
  );
  const linkCls = $derived(
    inverted
      ? 'font-medium underline decoration-current/50 underline-offset-2 transition-colors hover:decoration-current'
      : 'font-medium text-primary underline decoration-primary/30 underline-offset-2 transition-colors hover:decoration-primary',
  );
</script>

{#snippet inline(tokens: Inline[])}{#each tokens as tk}{#if tk.t === 'text'}{tk.v}{:else if tk.t === 'bold'}<strong class={boldCls}>{tk.v}</strong>{:else if tk.t === 'italic'}<em>{tk.v}</em>{:else if tk.t === 'code'}<code class={codeCls}>{tk.v}</code>{:else if tk.t === 'mention'}<span class={mentionCls}>@{tk.v}</span>{:else if tk.t === 'link'}<a href={tk.href} target="_blank" rel="noopener noreferrer" onclick={(e) => followLink(e, tk.href)} class={linkCls}>{tk.v}</a>{/if}{/each}{/snippet}

<!-- `.redact` on the CONTAINER, which is the exception to the mark-leaves rule and
     earns it: this is free prose, so there is no inner leaf holding "the value" — the
     whole block is the value. Every post body, comment body and DM message in both
     apps renders through here, which makes this one class the entire written-content
     half of private mode. Safe as a container because it holds only text, links and
     mentions — no popover whose positioning a `filter` could disturb. -->
<div class="redact skool-content {cls}">
  {#each blocks as b}
    {#if b.t === 'p'}
      <p class="mb-3 whitespace-pre-wrap break-words last:mb-0">{#each b.lines as line, i}{#if i > 0}<br />{/if}{@render inline(line)}{/each}</p>
    {:else if b.ordered}
      <ol class="mb-3 list-decimal space-y-1 pl-5 last:mb-0 marker:text-base-content/62" start={b.start}>{#each b.items as it}<li class="break-words pl-0.5">{@render inline(it)}</li>{/each}</ol>
    {:else}
      <ul class="mb-3 list-disc space-y-1 pl-5 last:mb-0 marker:text-base-content/54">{#each b.items as it}<li class="break-words pl-0.5">{@render inline(it)}</li>{/each}</ul>
    {/if}
  {/each}
</div>
